import type { ProvenanceType } from '@vivport/backend/types';
import { createPersistentStore } from './utilsStore';
import { DEFAULT_COMPOSER_LLM_MODEL_ID } from '@/features/speak2me/components/ChatComposerModels';

export type Speak2MeView = 'chat' | 'unread-list';

export interface ResumeProvenanceTarget {
  id: number;
  type: ProvenanceType;
  code: string;
}

interface Speak2MeUIState {
  isOpen: boolean;
  resumeNeoChatId: number | null;
  resumeProvenance: ResumeProvenanceTarget | null;
  openAsFreshChat: boolean;
  // One-shot intent consumed the moment the panel opens, so it is deliberately absent from
  // partialize: a reload must not replay auto-start with no user gesture behind it.
  openInDefaultInputMode: boolean;
  selectedLlmModelId: string;
  view: Speak2MeView;
  dictationDeviceId: string | null;
  setIsOpen: (isOpen: boolean) => void;
  setResumeNeoChatId: (neoChatId: number | null) => void;
  setResumeProvenance: (target: ResumeProvenanceTarget | null) => void;
  setOpenAsFreshChat: (flag: boolean) => void;
  setOpenInDefaultInputMode: (flag: boolean) => void;
  setSelectedLlmModelId: (id: string) => void;
  setView: (view: Speak2MeView) => void;
  setDictationDeviceId: (id: string | null) => void;
}

type Speak2MePersistedState = Pick<
  Speak2MeUIState,
  'isOpen' | 'resumeNeoChatId' | 'resumeProvenance' | 'openAsFreshChat' | 'view' | 'dictationDeviceId'
>;

export const useSpeak2MeStore = createPersistentStore<Speak2MeUIState, Speak2MePersistedState>(
  'speak2meData',
  (set) => ({
    isOpen: false,
    resumeNeoChatId: null,
    resumeProvenance: null,
    openAsFreshChat: false,
    openInDefaultInputMode: false,
    selectedLlmModelId: DEFAULT_COMPOSER_LLM_MODEL_ID,
    view: 'chat',
    dictationDeviceId: null,
    setIsOpen: (isOpen) => set({ isOpen }),
    setResumeNeoChatId: (neoChatId) => set({ resumeNeoChatId: neoChatId }),
    setResumeProvenance: (target) => set({ resumeProvenance: target }),
    setOpenAsFreshChat: (openAsFreshChat) => set({ openAsFreshChat }),
    setOpenInDefaultInputMode: (openInDefaultInputMode) => set({ openInDefaultInputMode }),
    setSelectedLlmModelId: (selectedLlmModelId) => set({ selectedLlmModelId }),
    setView: (view) => set({ view }),
    setDictationDeviceId: (dictationDeviceId) => set({ dictationDeviceId }),
  }),
  {
    version: 1,
    partialize: (state) => ({
      isOpen: state.isOpen,
      resumeNeoChatId: state.resumeNeoChatId,
      resumeProvenance: state.resumeProvenance,
      openAsFreshChat: state.openAsFreshChat,
      view: state.view,
      dictationDeviceId: state.dictationDeviceId,
    }),
    migrate: (persistedState) => {
      const { selectedLlmModelId, ...rest } = (persistedState ?? {}) as Speak2MePersistedState & {
        selectedLlmModelId?: unknown;
      };
      return rest;
    },
  },
);

declare global {
  interface Window {
    __vivportSpeak2MeStore?: typeof useSpeak2MeStore;
  }
}

if (import.meta.env.DEV) {
  window.__vivportSpeak2MeStore = useSpeak2MeStore;
}
